import { useCallback, useEffect, useRef, useState } from 'react';

/** Страница курсорного списка, как её отдаёт сервер. */
export interface ICursorPage<T> {
  items: T[];
  nextCursor: string | null;
  total: number | null;
}

export type CursorFetcher<T> = (
  cursor: string | null,
  limit: number,
) => Promise<ICursorPage<T>>;

interface IState<T> {
  key: string;
  items: T[];
  nextCursor: string | null;
  total: number | null;
  loading: boolean;
  loadingMore: boolean;
  error: boolean;
}

/** Предел страницы сервера (`PAGE_LIMIT_MAX` бэкенда). */
export const MAIL_PAGE_MAX = 100;

/**
 * ТЗ 0122 — курсорный список МИМО кеша Apollo: страницы копятся в
 * состоянии хука, а не склейкой `fetchMore` в кеше. Так не течёт фильтр в
 * фильтр (грабли `updateQuery`), и перечитывание после действия не
 * схлопывает открытые страницы: `reload` просит столько строк, сколько уже
 * показано (до предела сервера).
 *
 * `key` — отпечаток фильтров: новый ключ — список с начала. Ответ по
 * старому ключу (запрос в полёте) отбрасывается. Пока грузится новый
 * ключ, прежние строки не показываются (они от другого фильтра), а при
 * `reload` — остаются на экране до ответа.
 */
export const useCursorList = <T>(
  key: string,
  fetcher: CursorFetcher<T>,
  pageSize: number,
  skip = false,
) => {
  const [state, setState] = useState<IState<T>>({
    key: '',
    items: [],
    nextCursor: null,
    total: null,
    loading: !skip,
    loadingMore: false,
    error: false,
  });
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const keyRef = useRef(key);
  keyRef.current = key;
  const seq = useRef(0);

  const load = useCallback(async (limit: number, silent: boolean) => {
    seq.current += 1;
    const mine = seq.current;
    const forKey = keyRef.current;
    if (!silent) {
      setState((prev) => ({
        ...prev,
        key: forKey,
        items: prev.key === forKey ? prev.items : [],
        loading: true,
        error: false,
      }));
    }
    try {
      const page = await fetcherRef.current(null, limit);
      if (mine !== seq.current || forKey !== keyRef.current) return;
      setState({
        key: forKey,
        items: page.items,
        nextCursor: page.nextCursor,
        total: page.total,
        loading: false,
        loadingMore: false,
        error: false,
      });
    } catch (caught) {
      if (mine !== seq.current || forKey !== keyRef.current) return;
      setState((prev) => ({
        ...prev,
        key: forKey,
        loading: false,
        loadingMore: false,
        error: !silent || prev.items.length === 0,
      }));
    }
  }, []);

  useEffect(() => {
    if (skip) return;
    load(pageSize, false);
  }, [key, skip, pageSize, load]);

  /** Перечитать то, что уже показано (после действия, по ревизии). */
  const shownRef = useRef(0);
  shownRef.current = state.key === key ? state.items.length : 0;
  const reload = useCallback(() => {
    if (skip) return;
    load(Math.min(MAIL_PAGE_MAX, Math.max(pageSize, shownRef.current)), true);
  }, [skip, pageSize, load]);

  const loadMore = useCallback(async () => {
    const { nextCursor, loadingMore, key: forKey } = state;
    if (!nextCursor || loadingMore || forKey !== keyRef.current) return;
    const mine = seq.current;
    setState((prev) => ({ ...prev, loadingMore: true }));
    try {
      const page = await fetcherRef.current(nextCursor, pageSize);
      if (mine !== seq.current || forKey !== keyRef.current) return;
      setState((prev) => ({
        ...prev,
        items: [...prev.items, ...page.items],
        nextCursor: page.nextCursor,
        loadingMore: false,
      }));
    } catch (caught) {
      if (mine !== seq.current) return;
      setState((prev) => ({ ...prev, loadingMore: false }));
    }
  }, [state, pageSize]);

  /** Поправить строки сразу, до ответа сервера (оптимистично). */
  const update = useCallback((fn: (items: T[]) => T[]) => {
    setState((prev) => ({ ...prev, items: fn(prev.items) }));
  }, []);

  const current = state.key === key;
  return {
    items: current ? state.items : [],
    total: current ? state.total : null,
    hasMore: current && Boolean(state.nextCursor),
    loading: !skip && (!current || state.loading),
    loadingMore: state.loadingMore,
    error: current && state.error,
    reload,
    loadMore,
    update,
  };
};
